import {
  useAsync,
  useDeepCompareEffect,
  useLocalStorageValue,
  useMountEffect,
} from "@react-hookz/web";
import defaults from "lodash/defaults";
import { useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import type { DeepRequired } from "utility-types";

import { config } from "~/data";
import type { hooksAdvertisementCampaignConfigQuery } from "~/queries/__generated__/hooksAdvertisementCampaignConfigQuery.graphql";

export const useAdvertisementCampaignConfig = () => {
  const { set: setCachedConfig, value: cachedConfig } =
    useLocalStorageValue<RawAdvertisementCampaignConfig>("advertisement-campaign-config");

  const [resolvedConfig, setResolvedConfig] = useState<AdvertisementCampaignConfig>(
    parseCampaignConfig(cachedConfig) ?? defaultAdvertisementCampaignConfig,
  );
  const environment = useRelayEnvironment();
  const [{ result: remoteConfig }, { execute }] = useAsync(async () => {
    const data = await fetchQuery<hooksAdvertisementCampaignConfigQuery>(
      environment,
      graphql`
        query hooksAdvertisementCampaignConfigQuery($id: String) {
          appConfig(filter: { entityId: $id }) {
            json
          }
        }
      `,
      {
        id: config.advertisement.campaignConfigId,
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    if (!data?.appConfig?.json) return;
    return data.appConfig.json as RawAdvertisementCampaignConfig;
  });
  useMountEffect(execute);

  useDeepCompareEffect(() => {
    if (!remoteConfig) return;

    setResolvedConfig(parseCampaignConfig(remoteConfig) ?? defaultAdvertisementCampaignConfig);
    setCachedConfig(remoteConfig);
  }, [remoteConfig, setCachedConfig]);

  return resolvedConfig;
};

export type AdvertisementCampaignConfig = {
  autoRefresh: {
    disabledDirectCampaignLineItemIds: number[];
  };
  takeoverAd: {
    style: AdvertisementTakeoverAdSectionsConfig;
  };
};

export const defaultAdvertisementCampaignConfig: AdvertisementCampaignConfig = {
  autoRefresh: {
    disabledDirectCampaignLineItemIds: [],
  },
  takeoverAd: {
    style: {
      country: [],
      enableInPageTypes: {
        article: {
          enable: false,
        },
        mainSection: {
          enable: false,
        },
        subSection: {
          enable: false,
        },
      },
      enableTakeoverByDateTime: false,
      endDateTime: 0,
      ipAddress: [],
      startDateTime: 0,
      targetNonSubscribersOnly: false,
      targetSubscribersOnly: false,
    },
  },
};

type RawAdvertisementTakeoverAdSectionConfig = {
  country?: string[];
  enableInPageTypes?: {
    article?: {
      enable?: boolean;
    };
    mainSection: {
      enable?: boolean;
    };
    subSection: {
      enable?: boolean;
    };
  };
  enableTakeoverByDateTime?: boolean;
  endDateTime?: number;
  ipAddress?: string[];
  startDateTime?: number;
  targetNonSubscribersOnly?: boolean;
  targetSubscribersOnly?: boolean;
};

export type AdvertisementTakeoverAdSectionsConfig =
  DeepRequired<RawAdvertisementTakeoverAdSectionConfig>;

type RawAdvertisementCampaignConfig = {
  directCampaignLineItemIds?: number[];
  takeoverAd?: {
    style?: RawAdvertisementTakeoverAdSectionConfig;
  };
};

function parseCampaignConfig(
  raw?: RawAdvertisementCampaignConfig | null,
): AdvertisementCampaignConfig | undefined {
  if (!raw) return;

  const parsed: DeepPartial<AdvertisementCampaignConfig> = {
    autoRefresh: {
      disabledDirectCampaignLineItemIds: raw.directCampaignLineItemIds,
    },
    takeoverAd: {
      style: {
        country: raw.takeoverAd?.style?.country ?? [],
        enableInPageTypes: {
          article: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.article?.enable,
          },
          mainSection: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.mainSection?.enable,
          },
          subSection: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.subSection?.enable,
          },
        },
        enableTakeoverByDateTime: raw.takeoverAd?.style?.enableTakeoverByDateTime,
        endDateTime: raw.takeoverAd?.style?.endDateTime,
        ipAddress: raw.takeoverAd?.style?.ipAddress,
        startDateTime: raw.takeoverAd?.style?.startDateTime,
        targetNonSubscribersOnly: raw.takeoverAd?.style?.targetNonSubscribersOnly,
        targetSubscribersOnly: raw.takeoverAd?.style?.targetSubscribersOnly,
      },
    },
  };

  const merged = {
    autoRefresh: defaults(parsed.autoRefresh, defaultAdvertisementCampaignConfig.autoRefresh),
    takeoverAd: {
      style: defaults(
        parsed.takeoverAd?.style,
        defaultAdvertisementCampaignConfig.takeoverAd.style,
      ),
    },
  };

  return merged;
}
